import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { readConsent, setConsent, type ConsentState } from '@/lib/consent';

/**
 * GDPR cookie-consent banner.
 *
 * Renders a fixed-bottom card until the user answers. Choice is
 * persisted to localStorage via `setConsent`, which also fires the
 * `altai-consent` CustomEvent so the PostHog + GA4 loaders can
 * bootstrap themselves without a page reload.
 *
 * Sentry error tracking + Crisp live chat are exempt from this gate
 * and initialise unconditionally from main.tsx.
 */
export function CookieConsent() {
  const { t } = useTranslation('common');
  const [state, setState] = useState<ConsentState>(() => readConsent());

  useEffect(() => {
    if (state !== null) return;
    function onKey(evt: KeyboardEvent) {
      if (evt.key !== 'Escape') return;
      // Escape = "decide later" = decline. GDPR best practice is to
      // treat dismissal as denial rather than acceptance, so we
      // persist `declined` and keep analytics dormant.
      setConsent('declined');
      setState('declined');
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state]);

  if (state !== null) return null;

  function accept() {
    setConsent('accepted');
    setState('accepted');
  }

  function decline() {
    setConsent('declined');
    setState('declined');
  }

  return (
    <div
      role="dialog"
      aria-label={t('cookieConsent.ariaLabel')}
      aria-live="polite"
      className="fixed bottom-4 left-4 right-4 z-50 mx-auto flex max-w-3xl flex-col gap-3 rounded-lg border border-solid border-neutral-border bg-default-background p-4 shadow-lg sm:flex-row sm:items-center sm:justify-between sm:gap-6 sm:p-5"
    >
      <p className="text-body font-body text-default-font">
        {t('cookieConsent.message')}{' '}
        <a
          href="/#/privacy"
          className="underline decoration-dotted underline-offset-2 hover:text-default-font"
        >
          {t('cookieConsent.learnMore')}
        </a>
      </p>
      <div className="flex shrink-0 items-center gap-2">
        <button
          type="button"
          onClick={decline}
          className="rounded-md px-3 py-1.5 text-body-bold font-body-bold text-subtext-color hover:text-default-font"
        >
          {t('cookieConsent.decline')}
        </button>
        <button
          type="button"
          onClick={accept}
          className="rounded-md bg-brand-600 px-4 py-1.5 text-body-bold font-body-bold text-white hover:bg-brand-700"
        >
          {t('cookieConsent.accept')}
        </button>
      </div>
    </div>
  );
}

export default CookieConsent;
